import { Button, Divider, Grid, Menu, MenuItem, Select } from "@material-ui/core"
import { Close, Language } from "@material-ui/icons"
import { FormattedMessage, useIntl } from "react-intl"
import { HackleExperiment, HackleVariation, useTrack, useVariation } from "@hackler/react-sdk"
import { LangCode, Locale } from "../locale"
import { Link as LinkRouter, useLocation } from "react-router-dom"
import React, { PropsWithChildren, useEffect, useLayoutEffect, useState } from "react"
import { createMuiTheme, createStyles, makeStyles, Theme, ThemeProvider } from "@material-ui/core/styles"
import usePlatform, { DESKTOP_VIEW_WIDTH } from "../hooks/usePlatform"

import AppBar from "@material-ui/core/AppBar"
import CssBaseline from "@material-ui/core/CssBaseline"
import CtaMessage from "../components/units/CtaMessage"
import Drawer from "@material-ui/core/Drawer"
import { GAEvent } from "../modules/GoogleAnalytics"
import IconButton from "@material-ui/core/IconButton"
import List from "@material-ui/core/List"
import ListItem from "@material-ui/core/ListItem"
import ListItemText from "@material-ui/core/ListItemText"
import MenuIcon from "@material-ui/icons/Menu"
import ProgressButton from "../components/Button/ProgressButton"
import Toolbar from "@material-ui/core/Toolbar"
import Typography from "@material-ui/core/Typography"
import beta from "../assets/images/beta.png"
import clsx from "clsx"
import getDocsUrl from "../fixtures/url"
import hackle from "../components/svgs/HackleABTest.svg"
import { animateScroll as scroll } from "react-scroll/modules"
import useChannelTalk from "../hooks/useChannelTalk"
import { useHackleFeatureFlags } from "../hooks/useHackleFeatureFlags"
import useLogReferralKey from "../hooks/useLogReferralKey"
import useLoginPopup from "../hooks/useLoginPopup"
import useTour from "../hooks/useTour"
import { ADD_DATA_ANALYTICS_DETAIL_PAGE, CHANNEL_TALK_TEST_KEY } from "../abTestKey"
import BarBanner from "../components/Banner/BarBanner"
import { getFromSessionStorage, setToSessionStorage, storageKey } from "../utils/storage"
import ExternalLinkIcon from "../assets/images/icon/ExternalLinkIcon"
import newBadgeIcon from "../assets/images/icon/newBadgeIcon.png"

const koFontFamily = [
  "Spoqa Han Sans",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(",")

export const enFontFamily = [
  "Montserrat",
  "Poppins",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(",")

const enChildFontFamily = [
  "Poppins",
  "Montserrat",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(",")

export const theme = createMuiTheme({
  typography: {
    fontFamily: koFontFamily,
  },
  palette: {
    primary: {
      main: "#2962FF",
      light: "#E8EFFC",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: DESKTOP_VIEW_WIDTH ? DESKTOP_VIEW_WIDTH : 768,
      lg: 1280,
      xl: 1920,
    },
  },
  overrides: {
    MuiDialog: {
      paper: {
        margin: "16px",
      },
    },
  },
})

theme.typography.h2 = {
  fontWeight: 700,
  fontSize: "24px",
  letterSpacing: "-1px",
  textAlign: "center",
  "@media (min-width:850px)": {
    fontSize: "40px",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "40px",
  },
}
theme.typography.h3 = {
  fontSize: "16px",
  lineHeight: "24px",
  textAlign: "center",
  color: "rgba(0,0,0,0.6)",
  "@media (min-width:850px)": {
    fontSize: "20px",
    lineHeight: "36px",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "20px",
    lineHeight: "36px",
  },
}

export const enTheme = createMuiTheme({
  typography: {
    fontFamily: enFontFamily,
    h1: {
      fontFamily: enFontFamily,
    },
    h2: {
      fontFamily: enFontFamily,
    },
    h3: {
      fontFamily: enFontFamily,
    },
    h4: {
      fontFamily: enFontFamily,
    },
    h5: {
      fontFamily: enFontFamily,
    },
    h6: {
      fontFamily: enFontFamily,
    },
    caption: {
      fontFamily: enChildFontFamily,
    },
  },
  palette: {
    primary: {
      main: "#2962FF",
      light: "#E8EFFC",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: DESKTOP_VIEW_WIDTH ? DESKTOP_VIEW_WIDTH : 768,
      lg: 1280,
      xl: 1920,
    },
  },
  overrides: {
    MuiDialog: {
      paper: {
        margin: "16px",
      },
    },
  },
})

enTheme.typography.h2 = {
  fontWeight: 700,
  fontSize: "24px",
  letterSpacing: "-1px",
  textAlign: "center",
  "@media (min-width:850px)": {
    fontSize: "40px",
  },
  [enTheme.breakpoints.up("md")]: {
    fontSize: "40px",
  },
}
enTheme.typography.h3 = {
  fontSize: "16px",
  lineHeight: "24px",
  textAlign: "center",
  color: "rgba(0,0,0,0.6)",
  "@media (min-width:850px)": {
    fontSize: "20px",
    lineHeight: "36px",
  },
  [enTheme.breakpoints.up("md")]: {
    fontSize: "20px",
    lineHeight: "36px",
  },
}

const drawerWidth = 240

interface StyleProps {
  lang: string
}

const useStyles = makeStyles<Theme, StyleProps>((theme: any) =>
  createStyles({
    fixedTopArea: {
      position: "sticky",
      top: 0,
      zIndex: 1000,
    },
    boucherBanner: {
      "& .bannerTitle": {
        fontWeight: 700,
        fontSize: "12px",
        lineHeight: "16px",
        [theme.breakpoints.up("md")]: {
          fontSize: "14px",
          lineHeight: "18px",
        },
      },
      "& .bannerReadMore": {
        fontWeight: 500,
        fontSize: "12px",
        lineHeight: "16px",
        [theme.breakpoints.up("md")]: {
          fontSize: "14px",
          lineHeight: "18px",
        },
      },
      "& .bannerChip": {
        marginTop: "1.5px",
        minWidth: "64px",
        [theme.breakpoints.up("md")]: {
          marginTop: 0,
        },
      },
      "& .BarBanner-removeButton": {
        marginTop: "4px",
        [theme.breakpoints.up("md")]: {
          marginTop: 0,
        },
      },
    },
    appBar: {
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
      minHeight: "72px",
    },
    appBarShift: {
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: drawerWidth,
    },
    title: {
      flexGrow: 1,
    },
    logo: {
      width: 100,
      verticalAlign: "middle",
    },
    hide: {
      display: "none",
    },
    drawer: {
      width: "100%",
      flexShrink: 0,
    },
    drawerPaper: {
      width: "100%",
    },
    drawerHeader: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0, 1, 0, "20px"),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: "flex-start",
      minHeight: "72px",
    },
    drawerHeaderImg: {
      flexGrow: 1,
    },
    drawerHeaderButton: {
      padding: theme.spacing(3),
    },
    toolBar: {
      minHeight: "72px",
      maxWidth: "1200px",
      width: "100%",
      margin: "0 auto",
    },
    gnbItemList: {
      "& .MuiGrid-item": {
        padding: "0 20px",
      },
    },
    gnbDepth1: {
      position: "relative",
      "&:hover $gnbDepth2Wrapper": {
        display: "block",
      },
    },
    gnbDepth2Wrapper: {
      display: "none",
      position: "absolute",
      width: "196px",
      background: "#F9FAFB",
      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
      borderRadius: "0px 0px 4px 4px",
      padding: "16px 0 0",
      margin: 0,
      "& li": {
        padding: 0,
        margin: 0,
        listStyle: "none",
      },
      "& h6": {
        fontSize: "14px",
      },
    },
    gnbDepth2Link: {
      display: "flex",
      fontSize: "14px",
      padding: "0 16px 16px",
      color: "rgba(0, 0, 0, 0.87)",
      textDecoration: "none",
      "& img": {
        marginLeft: "8px",
      },
      "&:hover": {
        color: "#2962FF",
        fontWeight: "bold",
      },
    },
    gnbDepth2LinkForA: {
      display: "flex",
      padding: "0 16px 16px",
      textDecoration: "none",
      fontSize: "14px",
      color: "rgba(0, 0, 0, 0.87)",
      "&:hover": {
        color: "#2962FF",
        fontWeight: "bold",
      },
    },
    mobileGnbDepth2Wrapper: {
      padding: "0 0 0 20px",
      "& li": {
        paddingLeft: "20px",
        listStyle: "none",
        "& a": {
          display: "flex",
          fontSize: "16px",
          color: "rgba(0, 0, 0, 0.6)",
          textDecoration: "none",
          padding: "8px 0",
          "& img": {
            marginLeft: "8px",
          },
        },
      },
    },
    gnbItem: {
      cursor: "pointer",
      height: "72px",
      lineHeight: "72px",
    },
    gnbItemSelected: {
      color: "#2962FF",
      fontWeight: 700,
      borderBottom: "3px solid #2962FF",
    },
    list: {
      padding: theme.spacing(0, 1),
    },
    startAbtestBtn: {
      padding: "8px 16px",
      fontSize: "16px",
      boxShadow: "none",
      textTransform: "none",
    },
    content: {
      flexGrow: 1,
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflow: "hidden",
    },
    contentShift: {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: 0,
    },
    active: {
      color: "#2962FF",
      fontWeight: () => 700,
    },
    cursor: {
      cursor: "pointer",
    },
    mobileLangSelect: {
      fontSize: "14px",
    },
    langIcon: {
      display: "inline-block",
      marginTop: "-4px",
      color: "rgba(0,0,0,0.6)",
      verticalAlign: "middle",
    },
    a: {
      textDecoration: "none",
      color: "black",
      "&:visited": {
        color: "black",
      },
    },
    listItemText: {
      fontSize: "16px",
    },
    tourBtn: {
      borderRadius: 8,
      fontSize: "14px",
      boxShadow: "none",
      textTransform: "none",
      height: "38px",
    },
    ctaDivider: {
      margin: "0 12px",
      height: "26px",
    },
    signInBtn: {
      fontSize: "14px",
      textTransform: "none",
    },
    depth2Item: {
      "& svg": {
        marginLeft: 4,
      },
    },
    badge: {
      width: "30px",
      height: "100%",
      marginTop: 4,
    },
  })
)

interface Props {}

export default function MainFrame({ children }: PropsWithChildren<Props>) {
  useLogReferralKey()
  const track = useTrack()
  const intl = useIntl()
  const [open, setOpen] = React.useState(false)
  const [activeName, setActiveName] = useState("")
  const { isDesktop } = usePlatform()
  const loginPopup = useLoginPopup()
  const channelTalk = useChannelTalk()
  const location = useLocation()
  const [anchorLangEl, setAnchorLangEl] = useState<null | HTMLElement>(null)
  const isLangMenuOpen = Boolean(anchorLangEl)
  const isActive = (value: string) => location.pathname.includes(value)
  const { lang } = Locale.getDefault()
  const classes = useStyles({ lang })
  const [tourSubmitting, setTourSubmitting] = useState(false)
  const tour = useTour()
  const channelTalkWebinarVariation = useVariation(CHANNEL_TALK_TEST_KEY)
  const boucherBannerVisible = getFromSessionStorage(storageKey.BoucherBannerVisible) ?? true
  const { boucherBannerOn, shownFunnelBetaBadge } = useHackleFeatureFlags()
  const shownDataAnalyticsMenu = useVariation(ADD_DATA_ANALYTICS_DETAIL_PAGE) === "B"
  const pathName = useLocation().pathname

  useLayoutEffect(() => {
    scroll.scrollToTop({
      spy: false,
      smooth: false,
      offset: -50,
      duration: 0,
    })
  }, [location.pathname])

  useEffect(() => {
    channelTalk()
    if (channelTalkWebinarVariation === "B") {
      // @ts-ignore
      ChannelIO && ChannelIO("track", "show_webinar_message")
    }

    function onScroll() {
      const scrollY = window.scrollY
      if (scrollY < 1200) {
        if (!isSelected("DataVerification")) {
          handleSetActive("DataVerification")
        }
      }
    }

    window.addEventListener("scroll", onScroll)

    return () => {
      window.removeEventListener("scroll", onScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      //@ts-ignore
      analytics.track("homepage_view", { path: pathName })
    } catch (error) {}
  }, [pathName])

  const handleDrawerOpen = () => {
    setOpen(true)
  }
  const handleDrawerClose = () => {
    setOpen(false)
  }
  const isSelected = (name: string) => {
    return name === activeName
  }
  const handleSetActive = (to: string) => {
    setActiveName(to)
  }

  const handleLangMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorLangEl(e.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorLangEl(null)
  }

  return (
    <ThemeProvider theme={lang === "ko" ? theme : enTheme}>
      <div>
        <div className={classes.fixedTopArea}>
          {boucherBannerOn && boucherBannerVisible && location.pathname === "/ko/" && (
            <BarBanner
              backgroundColor="#FFF1ED"
              className={classes.boucherBanner}
              onClick={() => window.open("https://blog.hackle.io/k-voucher?utm_source=homepage", "_blank")}
              removable={true}
              onRemove={() => setToSessionStorage(storageKey.BoucherBannerVisible, false)}>
              <BarBanner.TitleChip className="bannerChip" backgroundColor="#FF6745">
                비대면 바우처
              </BarBanner.TitleChip>
              <div style={{ marginLeft: isDesktop ? "12px" : "4px" }}>
                <span className="bannerTitle">
                  <span style={{ color: "#FF5630" }}>70% 할인 금액</span>으로 핵클의 모든 서비스를 사용해보세요!
                </span>
                <span className="bannerReadMore">
                  <span style={{ textDecoration: "underline", margin: "0 4px" }}>자세히 보기</span> {">"}
                </span>
              </div>
            </BarBanner>
          )}
          <CssBaseline />
          <AppBar
            id={"main-container"}
            color={"inherit"}
            position="static"
            className={clsx(classes.appBar, {
              [classes.appBarShift]: open,
            })}>
            <Toolbar className={classes.toolBar}>
              <Typography className={classes.title}>
                <LinkRouter
                  to={Locale.localeUrl("/")}
                  onClick={() => {
                    scroll.scrollToTop({
                      spy: false,
                      smooth: false,
                      offset: -50,
                      duration: 0,
                    })
                    track({ key: "homepage_home_gnb_logo_click" })
                  }}
                  style={{ cursor: "pointer", padding: "10px 2px" }}>
                  <img src={hackle} alt={"hackle"} className={classes.logo} />
                </LinkRouter>
              </Typography>
              {isDesktop ? (
                <>
                  <Grid container justify={"flex-end"} alignItems="center" className={classes.gnbItemList}>
                    <Grid item className={classes.gnbDepth1}>
                      <LinkRouter
                        to={Locale.localeUrl("/service")}
                        className={classes.a}
                        onClick={() => {
                          GAEvent({
                            category: "gnb",
                            action: "click",
                            label: "product",
                          })
                          track({ key: "homepage_home_gnb_product_click" })
                        }}>
                        <Typography
                          variant={"subtitle1"}
                          className={clsx(classes.gnbItem, {
                            [classes.gnbItemSelected]: isActive("/service"),
                          })}>
                          <FormattedMessage id={"GNB_PRODUCT"} />
                        </Typography>
                      </LinkRouter>
                      <ul className={classes.gnbDepth2Wrapper}>
                        <li>
                          <LinkRouter
                            className={classes.gnbDepth2Link}
                            to={Locale.localeUrl("/service")}
                            onClick={() => {
                              track({ key: "homepage_home_gnb_service_ab_test_click" })
                            }}>
                            <FormattedMessage id={"ab_test"} />
                          </LinkRouter>
                        </li>
                        <li>
                          <LinkRouter
                            className={classes.gnbDepth2Link}
                            to={Locale.localeUrl("/service/feature-flags")}
                            onClick={() => {
                              track({ key: "homepage_home_gnb_service_feature_flag_click" })
                            }}>
                            <FormattedMessage id={"feature_flag"} />
                          </LinkRouter>
                        </li>
                        {shownDataAnalyticsMenu && (
                          <li>
                            <LinkRouter
                              className={classes.gnbDepth2Link}
                              to={Locale.localeUrl("/service/analytics")}
                              onClick={() => {
                                track({ key: "homepage_home_gnb_service_data_analytics_click" })
                              }}>
                              <FormattedMessage id={"data_analytics"} />{" "}
                              <img src={newBadgeIcon} className={classes.badge} alt={"new"} />
                            </LinkRouter>
                          </li>
                        )}
                        <li>
                          <LinkRouter
                            className={classes.gnbDepth2Link}
                            to={Locale.localeUrl("/service/funnel")}
                            onClick={() => {
                              track({ key: "homepage_home_gnb_service_funnel_click" })
                            }}>
                            <FormattedMessage id={"funnel"} />
                            {shownFunnelBetaBadge && <img src={beta} className={classes.badge} alt={"beta"} />}
                          </LinkRouter>
                        </li>
                      </ul>
                    </Grid>
                    <Grid item className={classes.gnbDepth1}>
                      <a
                        className={classes.a}
                        href={getDocsUrl("getting_started_product_guide")}
                        target={"_blank"}
                        rel="noopener noreferrer"
                        onClick={() => {
                          GAEvent({
                            category: "gnb",
                            action: "click",
                            label: "service_guide",
                          })
                          track({ key: "homepage_home_gnb_service_guide_click", properties: { type: "HEADER" } })
                        }}>
                        <Typography variant={"subtitle1"} className={clsx(classes.gnbItem)}>
                          <FormattedMessage id={"GNB_GUIDES"} />
                        </Typography>
                      </a>
                      <ul className={classes.gnbDepth2Wrapper}>
                        <li>
                          <a
                            className={classes.gnbDepth2LinkForA}
                            href={getDocsUrl("getting_started_product_guide")}
                            target={"_blank"}
                            rel="noopener noreferrer"
                            onClick={() => {
                              GAEvent({
                                category: "gnb",
                                action: "click",
                                label: "service_guide",
                              })
                              track({ key: "homepage_home_gnb_service_guide_click", properties: { type: "PRODUCT" } })
                            }}>
                            <Grid container alignItems={"center"} className={classes.depth2Item}>
                              <FormattedMessage id={"SERVICE_GUIDE"} /> <ExternalLinkIcon />
                            </Grid>
                          </a>
                        </li>
                        <li>
                          <a
                            className={classes.gnbDepth2LinkForA}
                            href={getDocsUrl("getting_started_developers_guide")}
                            target={"_blank"}
                            rel="noopener noreferrer"
                            onClick={() => {
                              GAEvent({
                                category: "gnb",
                                action: "click",
                                label: "developers_guide",
                              })
                              track({
                                key: "homepage_home_gnb_service_guide_click",
                                properties: { type: "DEVELOPER" },
                              })
                            }}>
                            <Grid container alignItems={"center"} className={classes.depth2Item}>
                              <FormattedMessage id={"DEVELOPERS_GUIDE"} /> <ExternalLinkIcon />
                            </Grid>
                          </a>
                        </li>
                      </ul>
                    </Grid>
                    <Grid item>
                      <LinkRouter
                        className={classes.a}
                        to={Locale.localeUrl("/pricing")}
                        onClick={() => {
                          GAEvent({
                            category: "gnb",
                            action: "click",
                            label: "pricing",
                          })
                          track({ key: "homepage_home_gnb_pricing_click" })
                        }}>
                        <Typography
                          variant={"subtitle1"}
                          className={clsx(classes.gnbItem, {
                            [classes.gnbItemSelected]: isActive("/pricing"),
                          })}>
                          <FormattedMessage id={"GNB_PRICING"} />
                        </Typography>
                      </LinkRouter>
                    </Grid>
                    <Grid item>
                      <a
                        className={classes.a}
                        href={getDocsUrl("blog")}
                        target={"_blank"}
                        rel="noopener noreferrer"
                        onClick={() => {
                          GAEvent({
                            category: "gnb",
                            action: "click",
                            label: "blog",
                          })
                          track({ key: "homepage_home_gnb_blog_click" })
                        }}>
                        <Typography variant={"subtitle1"} className={clsx(classes.gnbItem)}>
                          <FormattedMessage id={"GNB_BLOG"} />
                        </Typography>
                      </a>
                    </Grid>
                    <Grid item>
                      <div
                        onClick={(e) => {
                          GAEvent({
                            category: "gnb",
                            action: "click",
                            label: "language",
                          })
                          handleLangMenuOpen(e)
                        }}>
                        <Typography variant={"subtitle1"} className={classes.gnbItem}>
                          <Language fontSize={"small"} className={classes.langIcon} />{" "}
                          {lang === "ko" ? "한국어" : "English"}
                        </Typography>
                      </div>
                      <Menu
                        anchorEl={anchorLangEl}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        keepMounted
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        open={isLangMenuOpen}
                        onClose={handleMenuClose}>
                        <MenuItem
                          onClick={() => {
                            GAEvent({
                              category: "language",
                              action: "click",
                              label: "korean",
                            })
                            Locale.changeLangAndReload("ko")
                          }}>
                          한국어
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            GAEvent({
                              category: "language",
                              action: "click",
                              label: "english",
                            })
                            Locale.changeLangAndReload("en")
                          }}>
                          English
                        </MenuItem>
                      </Menu>
                    </Grid>
                    <HackleExperiment experimentKey={111}>
                      <HackleVariation variation={"A"}>
                        <Grid item>
                          <Typography variant={"subtitle1"} className={classes.gnbItem}>
                            <Button
                              color={"primary"}
                              variant={"contained"}
                              className={classes.startAbtestBtn}
                              onClick={() => {
                                GAEvent({
                                  category: "gnb",
                                  action: "click",
                                  label: "start abtest",
                                })
                                track({ key: "homepage_home_gnb_abtest_start_click" })
                                loginPopup()
                              }}>
                              <strong>
                                <CtaMessage />
                              </strong>
                            </Button>
                          </Typography>
                        </Grid>
                      </HackleVariation>
                      <HackleVariation variation={"B"}>
                        <Divider orientation="vertical" className={classes.ctaDivider} />
                        <Grid item style={{ marginRight: -8 }}>
                          <a
                            className={classes.a}
                            onClick={() => {
                              track({ key: "homepage_home_gnb_abtest_start_click" })
                              loginPopup()
                            }}>
                            <Typography variant={"subtitle1"} className={clsx(classes.gnbItem)}>
                              <FormattedMessage id={"sign_in"} />
                            </Typography>
                          </a>
                        </Grid>
                        <Grid item>
                          <Typography variant={"subtitle1"} className={classes.gnbItem}>
                            <ProgressButton
                              progress={tourSubmitting}
                              color={"primary"}
                              variant={"contained"}
                              className={classes.tourBtn}
                              onClick={() => {
                                setTourSubmitting(true)
                                track({
                                  key: "homepage_tour_click",
                                  properties: {
                                    area: "GNB",
                                  },
                                })

                                try {
                                  //@ts-ignore
                                  analytics.track("homepage_tour_click", { area: "GNB" })
                                } catch (error) {}

                                tour()
                              }}>
                              <strong>
                                <FormattedMessage id={"gnb_tour"} />
                              </strong>
                            </ProgressButton>
                          </Typography>
                        </Grid>
                      </HackleVariation>
                    </HackleExperiment>
                  </Grid>
                </>
              ) : (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerOpen}
                  className={clsx(open && classes.hide)}>
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </AppBar>
        </div>
        <main
          className={clsx(classes.content, {
            [classes.contentShift]: open,
          })}>
          {children}
        </main>
        {/*mobile_mainframe*/}
        <Drawer
          className={classes.drawer}
          variant="temporary"
          anchor="top"
          open={open}
          classes={{
            paper: classes.drawerPaper,
          }}>
          <div className={classes.drawerHeader}>
            <span className={classes.drawerHeaderImg}>
              <img src={hackle} alt={"hackle"} className={classes.logo} />
            </span>
            <IconButton onClick={handleDrawerClose}>
              <Close />
            </IconButton>
          </div>
          <List className={classes.list}>
            <LinkRouter
              to={Locale.localeUrl("/service")}
              onClick={() => {
                track({ key: "homepage_home_gnb_product_click" })
                handleDrawerClose()
              }}
              className={classes.a}>
              <ListItem button>
                <ListItemText
                  disableTypography
                  primary={intl.formatMessage({ id: "GNB_PRODUCT" })}
                  className={clsx({
                    [classes.active]: isActive("/service"),
                    [classes.listItemText]: true,
                  })}
                />
              </ListItem>
            </LinkRouter>
            <ul className={classes.mobileGnbDepth2Wrapper}>
              <li>
                <LinkRouter
                  to={Locale.localeUrl("/service")}
                  onClick={() => {
                    track({ key: "homepage_home_gnb_service_ab_test_click" })
                    handleDrawerClose()
                  }}>
                  <FormattedMessage id={"ab_test"} />
                </LinkRouter>
              </li>
              <li>
                <LinkRouter
                  to={Locale.localeUrl("/service/feature-flags")}
                  onClick={() => {
                    track({ key: "homepage_home_gnb_service_feature_flag_click" })
                    handleDrawerClose()
                  }}>
                  <FormattedMessage id={"feature_flag"} />
                </LinkRouter>
              </li>
              {shownDataAnalyticsMenu && (
                <li>
                  <LinkRouter
                    to={Locale.localeUrl("/service/analytics")}
                    onClick={() => {
                      track({ key: "homepage_home_gnb_service_data_analytics_click" })
                      handleDrawerClose()
                    }}>
                    <FormattedMessage id={"data_analytics"} />{" "}
                    <img src={newBadgeIcon} className={classes.badge} alt={"new"} />
                  </LinkRouter>
                </li>
              )}
              <li>
                <LinkRouter
                  to={Locale.localeUrl("/service/funnel")}
                  onClick={() => {
                    track({ key: "homepage_home_gnb_service_funnel_click" })
                    handleDrawerClose()
                  }}>
                  <FormattedMessage id={"funnel"} />
                  {shownFunnelBetaBadge && <img src={beta} className={classes.badge} alt={"beta"} />}
                </LinkRouter>
              </li>
            </ul>
            <ListItem button>
              <ListItemText
                disableTypography
                primary={intl.formatMessage({ id: "GNB_GUIDES" })}
                onClick={() => {
                  window.open(getDocsUrl("getting_started_product_guide"), "_blank")
                  GAEvent({
                    category: "gnb",
                    action: "click",
                    label: "service_guide",
                  })
                  track({ key: "homepage_home_gnb_service_guide_click", properties: { type: "HEADER" } })
                }}
                className={clsx({ [classes.listItemText]: true })}
              />
            </ListItem>
            <ul className={classes.mobileGnbDepth2Wrapper}>
              <li>
                <LinkRouter
                  to={Locale.localeUrl("/")}
                  onClick={() => {
                    window.open(getDocsUrl("getting_started_product_guide"), "_blank")
                    track({ key: "homepage_home_gnb_service_guide_click", properties: { type: "PRODUCT" } })
                    handleDrawerClose()
                  }}>
                  <Grid container alignItems={"center"} className={classes.depth2Item}>
                    <FormattedMessage id={"SERVICE_GUIDE"} /> <ExternalLinkIcon />
                  </Grid>
                </LinkRouter>
              </li>
              <li>
                <LinkRouter
                  to={Locale.localeUrl("/")}
                  onClick={() => {
                    window.open(getDocsUrl("getting_started_developers_guide"), "_blank")
                    track({ key: "homepage_home_gnb_service_guide_click", properties: { type: "DEVELOPER" } })
                    handleDrawerClose()
                  }}>
                  <Grid container alignItems={"center"} className={classes.depth2Item}>
                    <FormattedMessage id={"DEVELOPERS_GUIDE"} /> <ExternalLinkIcon />
                  </Grid>
                </LinkRouter>
              </li>
            </ul>
            <LinkRouter
              to={Locale.localeUrl("/pricing")}
              onClick={() => {
                track({ key: "homepage_home_gnb_pricing_click" })
                handleDrawerClose()
              }}
              className={classes.a}>
              <ListItem button>
                <ListItemText
                  disableTypography
                  primary={intl.formatMessage({ id: "GNB_PRICING" })}
                  className={clsx({
                    [classes.active]: isActive("/pricing"),
                    [classes.listItemText]: true,
                  })}
                />
              </ListItem>
            </LinkRouter>
            <ListItem button>
              <ListItemText
                disableTypography
                primary={intl.formatMessage({ id: "GNB_BLOG" })}
                onClick={() => {
                  window.open(getDocsUrl("blog"), "_blank")
                  GAEvent({
                    category: "gnb",
                    action: "click",
                    label: "blog",
                  })
                  track({ key: "homepage_home_gnb_blog_click" })
                }}
                className={clsx({ [classes.listItemText]: true })}
              />
            </ListItem>
            <ListItem>
              <Select
                disableUnderline
                className={classes.mobileLangSelect}
                defaultValue={lang}
                onChange={(e) => Locale.changeLangAndReload(e.target.value as LangCode)}>
                <MenuItem value={"ko"}>
                  <Language fontSize={"small"} className={classes.langIcon} /> 한국어
                </MenuItem>
                <MenuItem value={"en"}>
                  <Language fontSize={"small"} className={classes.langIcon} /> English
                </MenuItem>
              </Select>
            </ListItem>
          </List>
          <Grid container>
            <Grid item xs={12} className={classes.drawerHeaderButton}>
              <HackleExperiment experimentKey={111}>
                <HackleVariation variation={"A"}>
                  <Button
                    className={classes.startAbtestBtn}
                    variant={"contained"}
                    color={"primary"}
                    fullWidth
                    // href={dashboardUrl}
                    onClick={() => {
                      GAEvent({
                        category: "gnb",
                        action: "click",
                        label: "start abtest",
                      })
                      track({ key: "homepage_home_gnb_abtest_start_click" })
                      loginPopup()
                    }}>
                    <strong>
                      <CtaMessage />
                    </strong>
                  </Button>
                </HackleVariation>
                <HackleVariation variation={"B"}>
                  <Grid container direction={"row"}>
                    <Grid item xs={6}>
                      <Button
                        className={classes.signInBtn}
                        variant={"text"}
                        fullWidth
                        onClick={() => {
                          track({ key: "homepage_home_gnb_abtest_start_click" })
                          loginPopup()
                        }}>
                        <FormattedMessage id={"sign_in"} />
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <ProgressButton
                        progress={tourSubmitting}
                        color={"primary"}
                        variant={"contained"}
                        fullWidth
                        className={classes.tourBtn}
                        onClick={() => {
                          setTourSubmitting(true)
                          track({
                            key: "homepage_tour_click",
                            properties: {
                              area: "GNB",
                            },
                          })

                          try {
                            //@ts-ignore
                            analytics.track("homepage_tour_click", { area: "GNB" })
                          } catch (error) {}

                          tour()
                        }}>
                        <strong>
                          <FormattedMessage id={"gnb_tour"} />
                        </strong>
                      </ProgressButton>
                    </Grid>
                  </Grid>
                </HackleVariation>
              </HackleExperiment>
            </Grid>
          </Grid>
        </Drawer>
      </div>
    </ThemeProvider>
  )
}
